import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowRight, Star } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import type { Product } from '@/types';

interface ProductCardProps {
  product: Product;
}

function ProductPlaceholder({ name }: { name: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-rp-bone p-6">
      <div className="relative flex aspect-[3/4] w-16 items-center justify-center rounded-2xl border-2 border-rp-line bg-rp-paper shadow-sm">
        <div className="absolute inset-x-3 top-3 h-1.5 rounded-full bg-rp-leaf-soft" />
        <span className="font-mono text-xs font-bold tracking-tight text-rp-forest">AU</span>
        <div className="absolute inset-x-3 bottom-3 h-8 rounded-lg bg-rp-leaf-soft/40" />
      </div>
      <span className="max-w-[12ch] text-center font-mono text-[10px] font-bold uppercase tracking-wider text-rp-moss">
        {name}
      </span>
    </div>
  );
}

export default function ProductCard({ product }: ProductCardProps) {
  const addItem = useCartStore((s) => s.addItem);
  const { format } = useCurrencyStore();

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex h-full flex-col overflow-hidden rounded-[18px] border border-rp-line bg-rp-paper transition-all duration-200 hover:border-rp-forest/20 hover:shadow-card-hover active:scale-[0.985]"
    >
      <Link to={`/products/${product.slug}`} className="block">
        <div className="relative aspect-[5/4] overflow-hidden border-b border-rp-line-2 bg-rp-bone">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              className="rp-card-cover h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center p-4">
              <ProductPlaceholder name={product.name} />
            </div>
          )}
          {product.flags && product.flags.length > 0 && (
            <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
              {product.flags.map((flag) => (
                <span
                  key={flag}
                  className={`rounded-full px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white ${
                    flag === 'Best Seller' ? 'bg-rp-warn' : 'bg-rp-forest'
                  }`}
                >
                  {flag}
                </span>
              ))}
            </div>
          )}
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          {product.rating && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-rp-ink">
              <Star size={12} className="fill-rp-warn text-rp-warn" />
              {product.rating.score.toFixed(1)}
              <span className="text-rp-moss">({product.rating.count})</span>
            </span>
          )}
          {product.badges && product.badges.length > 0 && (
            <span className="text-xs text-rp-moss">· {product.badges.join(' · ')}</span>
          )}
        </div>
        <h3 className="mt-2 text-lg font-semibold leading-tight text-rp-ink">
          <Link to={`/products/${product.slug}`} className="hover:text-rp-forest">
            {product.name}
          </Link>
        </h3>
        <p className="mt-1 text-xs text-rp-moss">
          {product.dose}{product.form ? ` · ${product.form}` : ''}
        </p>

        <div className="mt-auto flex items-center justify-between pt-4">
          <div className="flex items-baseline gap-2">
            {product.priceFrom && <span className="text-sm text-rp-moss">From</span>}
            <span className="text-base font-bold text-rp-forest">{format(product.priceUsd)}</span>
            {product.compareAtPriceUsd && (
              <span className="text-sm text-rp-moss line-through">{format(product.compareAtPriceUsd)}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/products/${product.slug}`}
              className="rp-quick-add relative inline-flex h-9 items-center gap-1 rounded-full border border-rp-line bg-rp-paper px-3 font-mono text-[10px] font-bold uppercase tracking-wider text-rp-forest transition-colors hover:border-rp-forest hover:bg-rp-forest hover:text-rp-paper sm:h-10 sm:px-4"
            >
              View <ArrowRight size={12} />
            </Link>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addItem(product.id);
              }}
              className="rp-quick-add relative flex h-9 w-9 items-center justify-center rounded-full bg-rp-forest text-rp-paper transition-colors hover:bg-rp-forest-2 sm:h-10 sm:w-10"
              aria-label={`Add ${product.name} to cart`}
            >
              <ShoppingBag size={14} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
